<template>
  <div class="pa-0 ma-0">
    <v-subheader class="mb-1 ml-0 pl-0" style="height: auto">
      {{ $t("room.functional.preconditions") }}
    </v-subheader>

    <niko-textarea
      :rows="1"
      :value="test.test_requirements"
      @onChange="
        (value) => {
          const options = {
            index,
            attribute: 'test_requirements',
            newValue: value,
          };
          changeFunctionalTestAttribute(options);
        }
      "
      :placeholder="$t('room.functional.preconditions')"
    />

    <niko-toggle
      :value="test.daylight_control_enabled"
      :label="$t('room.functional.daylight.control.label')"
      @onChange="
        (value) => {
          const options = {
            index,
            attribute: 'daylight_control_enabled',
            newValue: value,
          };
          changeFunctionalTestAttribute(options);
        }
      "
    />

    <div v-if="!test.daylight_control_enabled" class="mt-6">
      <v-subheader class="mb-1 ml-0 pl-0" style="height: auto">
        {{ $t("room.functional.daylight.control.disabled") }}
      </v-subheader>
      <niko-textarea
        :rows="1"
        :value="test.daylight_control_disabled_comment"
        @onChange="
          (value) => {
            const options = {
              index,
              attribute: 'daylight_control_disabled_comment',
              newValue: value,
            };
            changeFunctionalTestAttribute(options);
          }
        "
        :placeholder="$t('room.functional.daylight.control.disabled')"
      />
    </div>

    <div v-if="test.daylight_control_enabled" class="ml-6">
      <niko-toggle
        :value="test.daylight_zones_enabled"
        :label="$t('room.functional.daylight.zones.label')"
        @onChange="
          (value) => {
            const options = {
              index,
              attribute: 'daylight_zones_enabled',
              newValue: value,
            };
            changeFunctionalTestAttribute(options);
          }
        "
      />
      <div v-if="!test.daylight_zones_enabled" class="mt-6">
        <v-subheader class="mb-1 ml-0 pl-0" style="height: auto">
          {{ $t("room.functional.daylight.zones.disabled") }}
        </v-subheader>

        <niko-textarea
          :rows="1"
          :value="test.daylight_zones_disabled_comment"
          @onChange="
            (value) => {
              const options = {
                index,
                attribute: 'daylight_zones_disabled_comment',
                newValue: value,
              };
              changeFunctionalTestAttribute(options);
            }
          "
          :placeholder="$t('room.functional.daylight.zones.disabled')"
        />
      </div>

      <div v-if="test.daylight_zones_enabled">
        <niko-slider
          :disabled="zonesUnknown"
          class="mt-3"
          :label="$t('room.functional.daylight.zones.slider')"
          :min="0"
          :max="4"
          step="1"
          :daylightZones="true"
          :value="test.required_number_of_zones"
          textFieldType="text"
          @onChange="
            (value) => {
              const options = {
                index,
                attribute: 'required_number_of_zones',
                newValue: value,
              };
              changeFunctionalTestAttribute(options);
            }
          "
        />
        <v-checkbox
          v-model="zonesUnknown"
          hide-details
          :label="$t('room.functional.daylight.zones.unknown')"
        />
      </div>
    </div>
    <niko-toggle
      :value="test.motion_detectors_enabled"
      :label="$t('room.functional.motion_detectors.label')"
      @onChange="
        (value) => {
          const options = {
            index,
            attribute: 'motion_detectors_enabled',
            newValue: value,
          };
          changeFunctionalTestAttribute(options);
        }
      "
    />
    <div v-if="!test.motion_detectors_enabled" class="mt-6">
      <v-subheader class="mb-1 ml-0 pl-0" style="height: auto">
        {{ $t("room.functional.motion_detectors.disabled") }}
      </v-subheader>

      <niko-textarea
        :rows="1"
        :value="test.motion_detectors_disabled_comment"
        @onChange="
          (value) => {
            const options = {
              index,
              attribute: 'motion_detectors_disabled_comment',
              newValue: value,
            };
            changeFunctionalTestAttribute(options);
          }
        "
        :placeholder="$t('room.functional.motion_detectors.disabled')"
      />
    </div>
    <div v-if="test.motion_detectors_enabled">
      <h2 class="mt-6">{{ $t("room.functional.light_turned_on.title") }}</h2>
      <v-radio-group v-model="light_on_automatic" hide-details>
        <v-radio
          value="automatic"
          :label="$t('room.functional.light_turned_on.automatically')"
        ></v-radio>
        <v-radio
          value="by_switch"
          :label="$t('room.functional.light_turned_on.switch')"
        ></v-radio>
      </v-radio-group>

      <niko-slider
        :label="$t('room.functional.light_turned_off')"
        class="mt-3"
        suffix="min"
        :min="5"
        :max="60"
        step="5"
        :value="test.light_off_delay"
        @onChange="
          (value) => {
            const options = {
              index,
              attribute: 'light_off_delay',
              newValue: value,
            };
            changeFunctionalTestAttribute(options);
          }
        "
      />
    </div>
    <v-btn icon class="mb-6" @click="deleteFunctionalTest(index)"
      ><v-icon>$iconDelete</v-icon></v-btn
    >
  </div>
</template>
<script>
import { mapMutations } from "vuex";
import NikoToggle from "../UI/NikoToggle";
import NikoTextarea from "../UI/NikoTextarea";
import NikoSlider from "../UI/NikoSlider";

export default {
  name: "functional-test-details",
  components: {
    NikoToggle,
    NikoSlider,
    NikoTextarea,
  },
  props: ["test", "index"],
  computed: {
    zonesUnknown: {
      get() {
        return this.test.required_number_of_zones_unknown;
      },
      set(value) {
        this.changeFunctionalTestAttribute({
          index: this.index,
          attribute: "required_number_of_zones_unknown",
          newValue: value,
        });
      },
    },
    light_on_automatic: {
      get() {
        return this.test.light_on_automatic ? "automatic" : "by_switch";
      },
      set(value) {
        this.changeFunctionalTestAttribute({
          index: this.index,
          attribute: "light_on_automatic",
          newValue: value === "automatic",
        });
      },
    },
  },
  methods: {
    ...mapMutations("roomStore", [
      "changeFunctionalTestAttribute",
      "deleteFunctionalTest",
    ]),
  },
};
</script>
